/**
 * useComparisons
 *
 * Manages the list of chart comparisons on the model page.
 * Enforces a configurable max limit and prevents duplicate entries.
 * Provides add, remove, and toggle operations.
 */

import { useState } from 'react';
import { COMPARISON_COLORS, MAX_COMPARISONS } from '../constants/models';

/**
 * @param {number} [maxLimit=MAX_COMPARISONS] - Maximum simultaneous comparisons
 * @returns {{
 *   comparisons: Array<{ id: number, code: string, name: string, price: string|number, color: string }>,
 *   addComparison: (item: { code: string, name: string, price: any }) => void,
 *   removeComparison: (id: number) => void,
 *   toggleComparison: (item: { code: string, name: string, price: any }) => void,
 *   maxReached: boolean,
 * }}
 */
const useComparisons = (maxLimit = MAX_COMPARISONS) => {
  const [comparisons, setComparisons] = useState([]);

  const getColor = (index) => COMPARISON_COLORS[index] ?? '#6B7280';

  const addComparison = (item) => {
    setComparisons(prev => {
      if (prev.length >= maxLimit) return prev;
      if (prev.some(c => c.code === item.code)) return prev;
      return [...prev, {
        id: Date.now(),
        code: item.code,
        name: item.name,
        price: item.price,
        color: getColor(prev.length),
      }];
    });
  };

  const removeComparison = (id) => {
    setComparisons(prev => prev.filter(c => c.id !== id));
  };

  const toggleComparison = (item) => {
    setComparisons(prev => {
      const existing = prev.find(c => c.code === item.code);
      if (existing) {
        return prev.filter(c => c.id !== existing.id);
      }
      if (prev.length >= maxLimit) return prev;
      return [...prev, {
        id: Date.now(),
        code: item.code,
        name: item.name,
        price: item.price,
        color: getColor(prev.length),
      }];
    });
  };

  return {
    comparisons,
    addComparison,
    removeComparison,
    toggleComparison,
    maxReached: comparisons.length >= maxLimit,
  };
};

export default useComparisons;
